import { vendorListMatchesCustomTagFilter } from "./myVendorsTagMatcher";
import {
  collectServiceNamesFromList,
  collectVendorNamesFromList,
  extractVendorListMembers,
} from "./myVendorListsUtils";

function normalizeSearch(value = "") {
  return String(value).trim().toLowerCase();
}

function normalizeServiceKey(value = "") {
  return String(value).trim().toLowerCase().replace(/new$/i, "");
}

/** Map selected RFI catalog ids (PageToolbarFilters V2) to display names for list matching. */
export function resolveSelectedRfiServiceNames(selectedRfiIds = [], rfis = []) {
  if (!selectedRfiIds?.length) return [];
  const idSet = new Set(selectedRfiIds.map((id) => String(id)));
  const names = new Set();

  (rfis || []).forEach((rfi) => {
    const rfiId = rfi.rfi_id ?? rfi.id;
    if (rfiId == null || !idSet.has(String(rfiId))) return;
    const name = rfi.rfi_name || rfi.name || rfi.services;
    if (name) names.add(String(name).trim());
  });

  return Array.from(names);
}

export function listMatchesSearch(list, searchQuery = "") {
  const query = normalizeSearch(searchQuery);
  if (!query) return true;
  if (normalizeSearch(list.name).includes(query)) return true;
  if (normalizeSearch(list.notes).includes(query)) return true;
  return false;
}

/**
 * List passes when ANY member carries ANY selected service (OR within section).
 * Accepts V2 toolbar selections (rfi_id[]) resolved via `rfis` catalog.
 */
export function listMatchesServicesFilter(list, selectedRfiIds = [], rfis = []) {
  if (!selectedRfiIds?.length) return true;

  const wantedKeys = new Set(
    resolveSelectedRfiServiceNames(selectedRfiIds, rfis).map(normalizeServiceKey)
  );
  selectedRfiIds.forEach((id) => wantedKeys.add(normalizeServiceKey(id)));

  const listServices = collectServiceNamesFromList(list);
  return listServices.some((service) => wantedKeys.has(normalizeServiceKey(service)));
}

/** List passes when ANY member name matches ANY selected vendor (OR within section). */
export function listMatchesVendorsFilter(list, selectedVendorNames = []) {
  if (!selectedVendorNames?.length) return true;
  const wanted = new Set(selectedVendorNames.map((name) => normalizeSearch(name)));
  const listVendors = collectVendorNamesFromList(list);
  return listVendors.some((name) => wanted.has(normalizeSearch(name)));
}

/**
 * List passes when ANY member carries ANY selected custom tag (OR within section).
 * Tags resolve from `vendorTagsMap` by provider id, falling back to inline member tags.
 */
export function listMatchesCustomTagsFilter(
  list,
  selectedTags = [],
  vendorTagsMap = {}
) {
  if (!selectedTags?.length) return true;

  const memberTagLists = extractVendorListMembers(list).map((member) => {
    if (member.providerId && vendorTagsMap[member.providerId]?.length) {
      return vendorTagsMap[member.providerId];
    }
    return member.tags || [];
  });

  return vendorListMatchesCustomTagFilter(memberTagLists, selectedTags);
}

/** Client-side filter pass for Vendor Lists tab (search + toolbar filters). */
export function applyVendorListsFilters(
  lists = [],
  {
    searchQuery = "",
    selectedIds = {},
    rfis = [],
    vendorTagsMap = {},
  } = {}
) {
  return lists.filter((list) => {
    if (!listMatchesSearch(list, searchQuery)) return false;
    if (!listMatchesServicesFilter(list, selectedIds.services, rfis)) return false;
    if (!listMatchesVendorsFilter(list, selectedIds.vendors)) return false;
    if (!listMatchesCustomTagsFilter(list, selectedIds.customTags, vendorTagsMap)) {
      return false;
    }
    return true;
  });
}
